import React, { useEffect, useCallback, useState } from "react"
import _take from "lodash/take"
import isEmpty from "lodash/isEmpty"
import { ConnectToInstagram } from "app/feeds/connect-to-instagram"
import { fetchProviders } from "app/feeds/fetch-providers"
import { createFeed } from "app/feeds/create-feed"
import { requestRenderer } from "../request-renderer"
import { useFetch } from "hooks/use-new-fetch"
import { useRequestReducer } from "hooks/use-requests"

const generateCSS = (value, options) => options[value]
const gridOrList = (layout, gridContent, listContent) => {
  return layout === "grid" ? gridContent : listContent
}

const update = (response, dispatch) => {
  if (response.items) {
    dispatch({ type: "RESOLVE", payload: response })
  } else {
    dispatch({ type: "EMPTY" })
  }
}

export function InstagramFeed({
  style,
  items,
  onChange,
  mode,
  element_id,
  user,
  defaults,
  defaultChildren,
  useChildElements,
  ...props
}) {
  const config = { ...defaults, ...props }
  const {
    columns,
    crop,
    layout,
    numberOfImages,
    radius,
    shadow,
    spacing
  } = config
  const { addChild } = useChildElements("parent_element_id", element_id)

  const [feedData, dispatch] = useRequestReducer({
    data: undefined,
    status: "loading"
  })
  const { status, data: feed, error } = feedData

  const [apiUpgradeFlag, setApiUpgradeFlag] = useState(false)

  const [instagramRequest] = useFetch("/instagram")

  const fetchInstagram = useCallback(async () => {
    const response = await instagramRequest.get()

    const valid = !(response.status >= 400 && response.status <= 499)

    return { valid, status: response.status }
  }, [instagramRequest])

  const afterAuth = useCallback(() => {
    const query = async () => {
      try {
        // Verify that IG was actually connected
        const { valid, status } = await fetchInstagram()

        if (!valid) {
          if (status === 403) {
            setApiUpgradeFlag(true)
          }

          dispatch({ type: "DISCONNECT" })
          return
        }

        dispatch({ type: "FETCH" })
        let feedRequest = createFeed(user.account_id)
        let result = await feedRequest({
          name: "Instagram",
          provider_name: "Instagram",
          type: "Instagram"
        })
        update(result, dispatch)
      } catch (err) {
        dispatch({ type: "DISCONNECT" })
      }
    }
    query()
  }, [fetchInstagram, dispatch, user.account_id])

  const createFeedIfInstagramAuthenticated = useCallback(() => {
    const getFeed = async () => {
      try {
        afterAuth()
      } catch (e) {
        dispatch({ type: "DISCONNECT" })
      }
    }
    getFeed()
  }, [afterAuth, dispatch])

  useEffect(() => {
    const getProviders = async () => {
      try {
        const { valid, status } = await fetchInstagram()

        if (!valid) {
          if (status === 403) {
            setApiUpgradeFlag(true)
          }

          dispatch({ type: "DISCONNECT" })
          return
        }

        let fetch = fetchProviders(user.account_id)
        let response = await fetch("Instagram")
        if (response.providers.length) {
          return update(response.providers[0].feed, dispatch)
        } else {
          return createFeedIfInstagramAuthenticated()
        }
      } catch (err) {
        dispatch({ type: "REJECT", payload: err.message })
      }
    }
    if (mode === "preview") {
      if (defaultChildren) {
        update(
          {
            items: defaultChildren.map((media, index) => ({
              ...media,
              element_id: index
            }))
          },
          dispatch
        )
      }
    } else {
      getProviders()
    }
  }, [
    createFeedIfInstagramAuthenticated,
    defaultChildren,
    dispatch,
    fetchInstagram,
    mode,
    user.account_id
  ])

  useEffect(() => {
    if (status !== "success") {
      return
    }
    let children = feed.items.reduce((finalArray, currentItem) => {
      // Prevent adding already existing items to the final array of items
      const itemExists = items.some(
        existing => existing.attachment_id === currentItem.attachment_id
      )
      if (itemExists) {
        return finalArray
      }
      return finalArray.concat({
        caption: currentItem.title ?? "",
        type: "Image",
        upload_file_name: currentItem.attachment.filename,
        src: currentItem.attachment.original_url,
        attachment_id: currentItem.attachment_id,
        order: currentItem.position,
        url: currentItem.properties.permalink
      })
    }, [])

    if (!isEmpty(children)) {
      addChild(children, 0)
    }

    onChange({
      username: feed.properties?.username,
      feedId: feed.id
    })
  }, [feed, status])

  const shadowAndRadius = () => {
    return {
      boxShadow: generateCSS(shadow, {
        none: "none",
        small: "0px 3px 4px rgba(0,0,0, 0.2)",
        large: "0px 3px 14px rgba(0,0,0, 0.1), 0 70px 80px rgba(0,0,0, 0.07)"
      }),
      borderRadius: generateCSS(radius, {
        none: "0px",
        small: "6px",
        large: "16px"
      })
    }
  }

  const styles = {
    ...style,
    display: gridOrList(layout, "grid", "block"),
    gridGap: `${spacing}px`,
    gridTemplateColumns: `repeat(${columns}, 1fr)`
  }

  const styleFigure = index =>
    gridOrList(
      layout,
      {
        position: "relative",
        margin: 0,
        height: "0",
        paddingBottom: generateCSS(crop, {
          square: "100%", // 1:1
          landscape: "75%", // 4:3
          portrait: "113%" // 3:4
        })
      },
      {
        ...shadowAndRadius(),
        background: "#fff",
        padding: "20px",
        // Behave like owl CSS selector by not adding top margin on first item
        marginTop: index === 0 ? null : `${spacing}px`
      }
    )

  return requestRenderer(
    status,
    { error },
    {
      idle: () => (
        <ConnectToInstagram
          afterAuth={afterAuth}
          apiUpgradeFlag={apiUpgradeFlag}
        />
      ),
      empty: () => (
        <div className="text-center border-gray-300 border-solid border-4 p-5 bg-gray-50 rounded-lg">
          <h2
            style={{
              color: "#3d3d3d",
              fontWeight: "600",
              fontSize: "28px",
              marginBottom: "20px"
            }}
          >
            No posts yet
          </h2>
          <p className="text-gray-700" style={{ marginBottom: 0 }}>
            We weren’t able to fetch any posts from your Instagram account. You
            can still configure your feed on the right. Items will show up here
            when you post.
          </p>
        </div>
      ),
      success: () => (
        <div style={styles}>
          {_take(items, numberOfImages).map((item, index) => (
            <figure key={item.element_id} style={styleFigure(index)}>
              <img
                src={item.src}
                alt={item.caption}
                style={{
                  ...gridOrList(layout, shadowAndRadius(), {}),
                  position: gridOrList(layout, "absolute", "static"),
                  width: "100%",
                  height: gridOrList(layout, "100%", "auto"),
                  objectFit: "cover",
                  objectPosition: "center"
                }}
              />
              {gridOrList(
                layout,
                null,
                <figcaption style={{ marginTop: "20px" }}>
                  {item.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )
    }
  )()
}
